import { BEIGE_300, CORAL_600, GRAY_700, GRAY_900 } from 'constants/styling/theme'
import { FC, useState } from 'react'
import { Link, Stack, Typography } from '@mui/material'

import { KeyboardEventKey } from 'constants/misc'
import { LanguageDropdown } from 'components/common/TopBar/LanguageDropdown'
import Logo from 'components/common/Logo/Logo'
import { MUIButton } from 'components/common/MUIButton'
import { MUIInputField } from 'components/common/MUIInputField'
import { Nullable } from 'models/helpers'
import { PageTransition } from 'utils/animations'
import { Path } from 'constants/router'
import { Link as RouterLink } from 'react-router-dom'
import isEmail from 'validator/lib/isEmail'
import { useRequestPasswordReset } from 'dataQueries/user.query'
import { useAuth0 } from 'utils/auth'
import { useTranslation } from 'react-i18next'

/** Link to the forgot-password page, with the e-mail field prefilled when the address is known. */
export const forgotPasswordPath = (email?: Nullable<string>) => email ? `${Path.FORGOT_PASSWORD}?${new URLSearchParams({ email })}` : Path.FORGOT_PASSWORD

/**
 * @component
 * Public page where anyone, logged in or not, asks for a password-reset e-mail.
 * The confirmation never reveals whether the address belongs to an account.
 *
 * @example
 * <ForgotPassword />
 */
export const ForgotPassword: FC = () => {
  const { t } = useTranslation(['authentication'])
  const { isAuthenticated } = useAuth0()
  const requestPasswordReset = useRequestPasswordReset()

  const [email, setEmail] = useState(() => new URLSearchParams(window.location.search).get('email') ?? '')
  const [isEmailInvalid, setIsEmailInvalid] = useState(false)

  const submit = () => {
    const trimmedEmail = email.trim()
    if (!isEmail(trimmedEmail)) {
      setIsEmailInvalid(true)
      return
    }
    requestPasswordReset.mutate({ email: trimmedEmail })
  }

  return (
    <PageTransition>
      <Stack
        position="relative"
        minHeight="100vh"
        gap="1.6rem"
        padding="2rem"
        alignItems="center"
        justifyContent="center"
        textAlign="center"
        bgcolor={BEIGE_300}
      >

        <Stack position="absolute" top="1rem" right="1rem">
          <LanguageDropdown />
        </Stack>

        <Logo width="10rem" height="1.2rem" />

        <Typography variant="display-md">{t('authentication:forgot_password_title')}</Typography>

        {requestPasswordReset.isSuccess &&
          <Typography variant="text-sm" color={GRAY_700} className="fs-exclude">
            {t('authentication:forgot_password_check_inbox', { email: email.trim() })}
          </Typography>
        }

        {!requestPasswordReset.isSuccess &&
          <Stack gap="1.6rem" width="100%" maxWidth="36rem">

            <Typography variant="text-sm" color={GRAY_700}>
              {t('authentication:forgot_password_description')}
            </Typography>

            <MUIInputField
              sensitive
              autoComplete="email"
              label={t('authentication:forgot_password_email_label')}
              value={email}
              isError={isEmailInvalid}
              errorText={isEmailInvalid ? t('authentication:forgot_password_invalid_email') : null}
              disabled={requestPasswordReset.isPending}
              onChange={e => {
                setEmail(e.target.value)
                setIsEmailInvalid(false)
              }}
              onKeyDown={e => e.key === KeyboardEventKey.ENTER && submit()}
            />

            {requestPasswordReset.isError &&
              <Typography variant="text-sm" color={CORAL_600} role="alert">
                {t('authentication:forgot_password_error')}
              </Typography>
            }

            <MUIButton
              fullWidth
              isLoading={requestPasswordReset.isPending}
              disabled={requestPasswordReset.isPending}
              onClick={submit}
            >
              {t('authentication:forgot_password_submit')}
            </MUIButton>

          </Stack>
        }

        <Link component={RouterLink} to={isAuthenticated ? Path.PROFILE : Path.INDEX} variant="text-xs" color={GRAY_900} underline="none">
          {t(isAuthenticated ? 'authentication:forgot_password_back_to_profile' : 'authentication:forgot_password_back_to_login')}
        </Link>

      </Stack>
    </PageTransition>
  )
}
